import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  ArrowRight,
  Calculator,
  Lightbulb,
  Loader2,
  Navigation,
  Plus,
  RefreshCw,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";

import ModalBase from "components/ModalBase";
import ModalRealizarCotacao from "./ModalRealizarCotacao";
import { getCaixas, type Caixa } from "services/caixas";
import {
  getOpcoesSimulacao,
  mensagemErroSimulacao,
  simularEnvio,
  type FreteSimulado,
  type LimitesSimulacao,
  type PaisSimulacao,
  type SimulacaoResultado,
  type TotalSimulado,
} from "services/simulacao";

type Props = {
  show: boolean;
  title: string;
  setShow?: (value: React.SetStateAction<boolean>) => void;
  setShowNumber?: (value: React.SetStateAction<number>) => void;
  logo?: ReactNode;
};

/** Linha da tabela de caixas. Fica como string porque vem direto do input. */
type LinhaCaixa = {
  id: string;
  comprimento: string;
  largura: string;
  altura: string;
  peso: string;
  quantidade: string;
};

const COLUNAS_CAIXA: {
  campo: keyof Omit<LinhaCaixa, "id">;
  rotulo: string;
  rotuloLongo: string;
  placeholder: string;
  inputMode: "decimal" | "numeric";
}[] = [
  { campo: "comprimento", rotulo: "C (cm)", rotuloLongo: "Comprimento em cm", placeholder: "30", inputMode: "decimal" },
  { campo: "largura", rotulo: "L (cm)", rotuloLongo: "Largura em cm", placeholder: "20", inputMode: "decimal" },
  { campo: "altura", rotulo: "A (cm)", rotuloLongo: "Altura em cm", placeholder: "15", inputMode: "decimal" },
  { campo: "peso", rotulo: "Peso kg", rotuloLongo: "Peso em kg", placeholder: "1,5", inputMode: "decimal" },
  { campo: "quantidade", rotulo: "Qtd", rotuloLongo: "Quantidade", placeholder: "1", inputMode: "numeric" },
];

const novaLinha = (): LinhaCaixa => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  comprimento: "",
  largura: "",
  altura: "",
  peso: "",
  quantidade: "1",
});

const LIMITES_FALLBACK: LimitesSimulacao = {
  maxCaixas: 20,
  pesoMinKg: 0.1,
  pesoMaxKg: 70,
  dimMinCm: 1,
  dimMaxCm: 274,
  valorMin: 1,
  valorMax: 1000000,
};

/**
 * Aceita vírgula decimal — o cliente digita "1,5".
 * Campo vazio vira NaN de propósito: Number("") é 0, e isso faria uma caixa em
 * branco passar como preenchida na validação do form.
 */
const paraNumero = (v: string): number => {
  const bruto = String(v ?? "").trim();
  if (!bruto) return NaN;
  const n = Number(bruto.replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};

const moedaFmt = (valor: number | null | undefined, moeda: string): string => {
  if (valor === null || valor === undefined || !Number.isFinite(valor)) return "—";

  // Intl estoura se a transportadora devolver um código de moeda inesperado.
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: moeda }).format(
      valor,
    );
  } catch {
    return `${moeda} ${valor.toFixed(2)}`;
  }
};

/** "US$ 34,94" no padrão do desenho, que é mais curto que o do Intl. */
const usd = (v: number | null | undefined): string =>
  v === null || v === undefined || !Number.isFinite(v)
    ? "—"
    : `US$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const kg = (v: number) =>
  v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 });

const NOME_CARRIER: Record<string, string> = { FEDEX: "FedEx", UPS: "UPS" };
const COR_CARRIER: Record<string, string> = { FEDEX: "#4D148C", UPS: "#B45309" };
const SIGLA_CARRIER: Record<string, string> = { FEDEX: "FX", UPS: "UPS" };

const paisesParaOptions = (lista: PaisSimulacao[]) =>
  lista.map((p) => ({ value: p.code, label: p.nome }));

const ModalSimulacao = ({ show, title, setShow, setShowNumber, logo }: Props) => {
  const [origens, setOrigens] = useState<PaisSimulacao[]>([]);
  const [destinos, setDestinos] = useState<PaisSimulacao[]>([]);
  const [moedas, setMoedas] = useState<string[]>(["USD"]);
  const [limites, setLimites] = useState<LimitesSimulacao>(LIMITES_FALLBACK);
  const [cadastroCaixas, setCadastroCaixas] = useState<Caixa[]>([]);

  const [origem, setOrigem] = useState("BR");
  const [destino, setDestino] = useState("");
  const [caixas, setCaixas] = useState<LinhaCaixa[]>([novaLinha()]);
  const [valorDeclarado, setValorDeclarado] = useState("");
  const [moeda, setMoeda] = useState("USD");
  const [descricao, setDescricao] = useState("");
  const [ncm, setNcm] = useState("");

  const [carregando, setCarregando] = useState(false);
  const [resultado, setResultado] = useState<SimulacaoResultado | null>(null);
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const [novaCotacao, setNovaCotacao] = useState(false);

  useEffect(() => {
    if (!show) return;

    getOpcoesSimulacao()
      .then((res) => {
        setOrigens(res.origens ?? []);
        setDestinos(res.destinos ?? []);
        setMoedas(res.moedas?.length ? res.moedas : ["USD"]);
        setLimites(res.limites ?? LIMITES_FALLBACK);
        // Só uma origem habilitada hoje (Brasil): já deixa escolhida.
        if (res.origens?.length === 1) setOrigem(res.origens[0].code);
      })
      .catch(() => toast.error("Não foi possível carregar os países do simulador."));

    // Falha sozinho: sem o cadastro a pessoa ainda digita as medidas à mão.
    getCaixas().then(setCadastroCaixas).catch(() => undefined);
  }, [show]);

  const origemOptions = useMemo(() => paisesParaOptions(origens), [origens]);
  const destinoOptions = useMemo(() => paisesParaOptions(destinos), [destinos]);

  const caixasPreenchidas = caixas.every(
    (c) =>
      Number.isFinite(paraNumero(c.comprimento)) &&
      Number.isFinite(paraNumero(c.largura)) &&
      Number.isFinite(paraNumero(c.altura)) &&
      Number.isFinite(paraNumero(c.peso)),
  );

  const valorNum = paraNumero(valorDeclarado);
  const valorInvalido = valorDeclarado.trim() !== "" && !Number.isFinite(valorNum);

  const pendencias = useMemo(() => {
    const faltando: string[] = [];
    if (!origem) faltando.push("o país de origem");
    if (!destino) faltando.push("o país de destino");
    if (!caixasPreenchidas) faltando.push("as medidas e o peso de todas as caixas");
    if (!Number.isFinite(valorNum) || valorNum <= 0) {
      faltando.push("o valor declarado (apenas números, ex.: 250)");
    }
    return faltando;
  }, [origem, destino, caixasPreenchidas, valorNum]);

  const formValido = pendencias.length === 0;

  const atualizarCaixa = (id: string, campo: keyof Omit<LinhaCaixa, "id">, valor: string) =>
    setCaixas((prev) => prev.map((c) => (c.id === id ? { ...c, [campo]: valor } : c)));

  const adicionarCaixa = () => {
    if (caixas.length >= limites.maxCaixas) {
      toast.warning(`Máximo de ${limites.maxCaixas} caixas por simulação.`);
      return;
    }
    setCaixas((prev) => [...prev, novaLinha()]);
  };

  const removerCaixa = (id: string) =>
    setCaixas((prev) => (prev.length === 1 ? prev : prev.filter((c) => c.id !== id)));

  /** Preenche uma linha a partir do cadastro de caixas. */
  const usarCaixaCadastrada = (idCaixa: string) => {
    const caixa = cadastroCaixas.find((c) => String(c.id) === idCaixa);
    if (!caixa) return;

    const linha: LinhaCaixa = {
      ...novaLinha(),
      comprimento: String(caixa.comprimento),
      largura: String(caixa.largura),
      altura: String(caixa.altura),
      // O cadastro guarda a tara. O simulador pede o peso do volume pronto,
      // então entra como ponto de partida e a pessoa soma o produto.
      peso: String(caixa.peso),
    };

    setCaixas((prev) => {
      const primeira = prev[0];
      const vazia =
        primeira &&
        !primeira.comprimento &&
        !primeira.largura &&
        !primeira.altura &&
        !primeira.peso;
      return vazia ? [linha, ...prev.slice(1)] : [...prev, linha];
    });
  };

  const simular = async () => {
    if (!formValido || carregando) return;

    setCarregando(true);
    setResultado(null);
    setEscolhido(null);

    try {
      const res = await simularEnvio({
        origem,
        destino,
        moeda,
        valorDeclarado: paraNumero(valorDeclarado),
        descricao: descricao.trim() || undefined,
        ncm: ncm.trim() || undefined,
        caixas: caixas.map((c) => ({
          comprimento: paraNumero(c.comprimento),
          largura: paraNumero(c.largura),
          altura: paraNumero(c.altura),
          peso: paraNumero(c.peso),
          quantidade: Math.trunc(paraNumero(c.quantidade)) || 1,
        })),
      });

      setResultado(res);
      setEscolhido(melhorTotal(res.totais)?.carrier ?? res.fretes?.[0]?.carrier ?? null);
    } catch (err) {
      toast.error(mensagemErroSimulacao(err));
    } finally {
      setCarregando(false);
    }
  };

  const fechar = () => {
    if (setShow) setShow(false);
    if (setShowNumber) setShowNumber(-1);
  };

  const totalEscolhido = resultado?.totais.find((t) => t.carrier === escolhido);
  const freteEscolhido = resultado?.fretes.find((f) => f.carrier === escolhido);

  return (
    <>
      <ModalBase
        show={show}
        onClose={fechar}
        titulo={title}
        icone={logo ?? <Calculator />}
        subtitulo="Compare transportadoras e custo total antes de cotar. Não reserva tarifa."
        largura="1040px"
        corpoSemPadding
        rodape={
          resultado ? (
            <div className="flex items-center justify-between gap-4 border-t border-slate-100 bg-white px-6 py-3.5">
              <button
                onClick={fechar}
                className="text-sm text-slate-500 transition-colors hover:text-slate-700"
              >
                Fechar
              </button>
              <div className="flex items-center gap-3">
                {freteEscolhido && (
                  <span className="text-xs text-slate-500">
                    Selecionado:{" "}
                    <span className="font-medium text-slate-700">
                      {NOME_CARRIER[freteEscolhido.carrier]} {freteEscolhido.servico}
                    </span>
                    {totalEscolhido?.total_usd != null && ` · ${usd(totalEscolhido.total_usd)}`}
                  </span>
                )}
                <button
                  onClick={() => setNovaCotacao(true)}
                  disabled={!escolhido}
                  className="flex h-10 items-center gap-2 rounded-lg bg-brand-sidebar px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                >
                  Criar cotação com {escolhido ? NOME_CARRIER[escolhido] : ""}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : undefined
        }
      >
        <div className="grid h-[520px] grid-cols-1 md:grid-cols-[340px_1fr]">
          {/* FORMULÁRIO */}
          <div className="flex min-h-0 flex-col border-slate-100 md:border-r">
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
              <Bloco titulo="Rota">
                <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
                  <Seletor
                    label="Origem"
                    valor={origem}
                    aoMudar={setOrigem}
                    opcoes={origemOptions}
                    placeholder="Origem"
                  />
                  <Navigation className="mb-2.5 h-4 w-4 rotate-45 text-brand-primary" />
                  <Seletor
                    label="Destino"
                    valor={destino}
                    aoMudar={setDestino}
                    opcoes={destinoOptions}
                    placeholder="Escolha o país"
                  />
                </div>
              </Bloco>

              <Bloco titulo="Caixas">
                <div className="grid grid-cols-[repeat(5,1fr)_24px] gap-1.5 px-0.5 text-[9px] uppercase tracking-wide text-slate-400">
                  {COLUNAS_CAIXA.map((col) => (
                    <span key={col.campo}>{col.rotulo}</span>
                  ))}
                  <span />
                </div>

                <div className="mt-1 space-y-1.5">
                  {caixas.map((linha) => (
                    <div
                      key={linha.id}
                      className="grid grid-cols-[repeat(5,1fr)_24px] items-center gap-1.5"
                    >
                      {COLUNAS_CAIXA.map((col) => (
                        <input
                          key={col.campo}
                          value={linha[col.campo]}
                          onChange={(e) => atualizarCaixa(linha.id, col.campo, e.target.value)}
                          inputMode={col.inputMode}
                          aria-label={col.rotuloLongo}
                          placeholder={col.placeholder}
                          className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-center text-xs text-slate-700 outline-none transition-colors placeholder:text-slate-300 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => removerCaixa(linha.id)}
                        disabled={caixas.length === 1}
                        aria-label="Remover caixa"
                        className="grid h-6 w-6 place-items-center rounded text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-25 disabled:hover:bg-transparent"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={adicionarCaixa}
                    className="flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline"
                  >
                    <Plus className="h-3 w-3" />
                    Adicionar caixa
                  </button>

                  {cadastroCaixas.length > 0 && (
                    <label className="text-xs text-slate-500">
                      <span className="sr-only">Usar caixa cadastrada</span>
                      <select
                        value=""
                        onChange={(e) => e.target.value && usarCaixaCadastrada(e.target.value)}
                        className="cursor-pointer bg-transparent text-xs text-slate-500 underline-offset-2 outline-none hover:underline"
                      >
                        <option value="">Usar caixa cadastrada</option>
                        {cadastroCaixas.map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {c.cod_identificacao} — {c.descricao}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
              </Bloco>

              <Bloco titulo="Mercadoria">
                <div>
                  <span className="mb-1.5 block text-[11px] font-medium text-slate-500">
                    Valor declarado
                  </span>
                  <div
                    className={`flex h-10 items-center rounded-lg border bg-white transition-colors focus-within:ring-1 ${
                      valorInvalido
                        ? "border-red-300 focus-within:border-red-400 focus-within:ring-red-300"
                        : "border-slate-200 focus-within:border-brand-primary focus-within:ring-brand-primary"
                    }`}
                  >
                    <select
                      value={moeda}
                      onChange={(e) => setMoeda(e.target.value)}
                      aria-label="Moeda"
                      className="h-full cursor-pointer rounded-l-lg border-r border-slate-200 bg-slate-50 px-2 text-xs font-medium text-slate-600 outline-none"
                    >
                      {moedas.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <input
                      value={valorDeclarado}
                      onChange={(e) => setValorDeclarado(e.target.value)}
                      inputMode="decimal"
                      placeholder="234,00"
                      className="h-full flex-1 rounded-r-lg px-3 text-sm text-slate-700 outline-none placeholder:text-slate-300"
                    />
                  </div>
                  {valorInvalido && (
                    <p className="mt-1 text-[11px] text-red-600">Use apenas números.</p>
                  )}
                </div>

                <Campo
                  label="Descrição"
                  sufixoLabel="(opcional)"
                  valor={descricao}
                  aoMudar={setDescricao}
                  placeholder="Ex.: peças de vestuário"
                />

                <div>
                  <Campo
                    label="NCM / HS Code"
                    sufixoLabel="(recomendado)"
                    valor={ncm}
                    aoMudar={setNcm}
                    placeholder="6109.10.00"
                  />
                  <p className="mt-1 text-[11px] text-amber-600">
                    Com o NCM a estimativa de impostos fica muito mais precisa.
                  </p>
                </div>
              </Bloco>
            </div>

            <div className="shrink-0 border-t border-slate-100 px-5 py-4">
              {!formValido && !carregando && (
                <p className="mb-2 text-[11px] text-slate-400">Falta {pendencias[0]}.</p>
              )}
              <button
                onClick={simular}
                disabled={!formValido || carregando}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand-sidebar text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {carregando ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {carregando
                  ? "Simulando…"
                  : resultado
                    ? "Simular de novo"
                    : "Simular frete"}
              </button>
            </div>
          </div>

          {/* RESULTADO */}
          <div className="min-h-0 overflow-y-auto bg-slate-50/60 px-5 py-5">
            {carregando ? (
              <Aguardando />
            ) : !resultado ? (
              <Vazio />
            ) : (
              <Resultado
                res={resultado}
                escolhido={escolhido}
                onEscolher={setEscolhido}
                onTentarDeNovo={simular}
              />
            )}
          </div>
        </div>
      </ModalBase>

      {/* Os dados da simulação não são transportados: a cotação é um fluxo
          próprio, com remetente, destinatário e pedido. O botão leva para lá
          com a transportadora já decidida na cabeça de quem simulou. */}
      <ModalRealizarCotacao
        show={novaCotacao}
        setShow={setNovaCotacao}
        title="Realizar cotação"
        logo={<Calculator />}
      />
    </>
  );
};

export default ModalSimulacao;

/* ------------------------------------------------------------------ */
/* Resultado                                                           */
/* ------------------------------------------------------------------ */

/** Menor total estimado, ignorando quem veio sem número. */
const melhorTotal = (totais: TotalSimulado[]): TotalSimulado | null => {
  const comValor = totais.filter((t) => t.total_usd != null);
  if (!comValor.length) return null;
  return comValor.reduce((a, b) => ((a.total_usd ?? 0) <= (b.total_usd ?? 0) ? a : b));
};

const melhorFrete = (totais: TotalSimulado[]): TotalSimulado | null => {
  const comValor = totais.filter((t) => t.frete_usd != null);
  if (!comValor.length) return null;
  return comValor.reduce((a, b) => ((a.frete_usd ?? 0) <= (b.frete_usd ?? 0) ? a : b));
};

const Resultado = ({
  res,
  escolhido,
  onEscolher,
  onTentarDeNovo,
}: {
  res: SimulacaoResultado;
  escolhido: string | null;
  onEscolher: (c: string) => void;
  onTentarDeNovo: () => void;
}) => {
  const barato = melhorFrete(res.totais);
  const barateiro = melhorTotal(res.totais);
  const maisRapido = res.fretes.length
    ? res.fretes.reduce((a, b) => (a.prazo_dias <= b.prazo_dias ? a : b))
    : null;

  // Vale a pena dizer quando o frete mais barato não é o custo mais baixo: é
  // o desembaraço fixo que vira o jogo, e ele não aparece na tabela de frete.
  const viradaDeJogo =
    barato && barateiro && barato.carrier !== barateiro.carrier
      ? {
          frete: NOME_CARRIER[barato.carrier],
          total: NOME_CARRIER[barateiro.carrier],
          diferenca:
            (barato.total_usd ?? 0) - (barateiro.total_usd ?? 0),
        }
      : null;

  const semImposto = res.totais.every((t) => t.imposto_usd == null);

  return (
    <div className="space-y-3">
      {/* Resumo da entrada */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
        <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold text-white">
          {res.entrada.origem.code}
        </span>
        <span className="rounded bg-brand-primary px-1.5 py-0.5 text-[10px] font-bold text-white">
          {res.entrada.destino.code}
        </span>
        <span className="text-[11px] text-slate-500">
          {res.entrada.volumes} {res.entrada.volumes === 1 ? "volume" : "volumes"} · peso
          cobrado <strong className="text-slate-700">{kg(res.entrada.peso_taxavel_kg)} kg</strong>{" "}
          (real {kg(res.entrada.peso_real_kg)} · cubado {kg(res.entrada.peso_cubado_kg)}) ·
          valor {moedaFmt(res.entrada.valor_declarado, res.entrada.moeda)}
        </span>
      </div>

      {viradaDeJogo && Number.isFinite(viradaDeJogo.diferenca) && (
        <div className="flex items-start gap-2.5 rounded-xl bg-brand-sidebar px-3 py-2.5 text-white">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
          <p className="text-[11px] leading-relaxed">
            A {viradaDeJogo.frete} tem o frete mais barato, mas com o desembaraço a{" "}
            {viradaDeJogo.total} sai {usd(Math.abs(viradaDeJogo.diferenca))} mais barata no
            total.
          </p>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {res.fretes.map((frete) => (
          <CartaoCarrier
            key={frete.carrier}
            frete={frete}
            total={res.totais.find((t) => t.carrier === frete.carrier)}
            selecionado={escolhido === frete.carrier}
            onSelecionar={() => onEscolher(frete.carrier)}
            menorFrete={barato?.carrier === frete.carrier}
            menorTotal={barateiro?.carrier === frete.carrier}
            maisRapido={maisRapido?.carrier === frete.carrier}
          />
        ))}
      </div>

      {semImposto && (
        <div className="flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
          <p className="flex gap-2 text-[11px] leading-relaxed text-amber-900">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Impostos de {res.entrada.destino.nome} indisponíveis agora. Os totais acima são
              frete + desembaraço, sem impostos do destino.
            </span>
          </p>
          <button
            onClick={onTentarDeNovo}
            className="shrink-0 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-[11px] font-medium text-amber-800 transition-colors hover:bg-amber-100"
          >
            Tentar de novo
          </button>
        </div>
      )}

      {res.avisos?.map((aviso) => (
        <p key={aviso} className="text-[11px] leading-relaxed text-slate-400">
          {aviso}
        </p>
      ))}

      <p className="text-[11px] leading-relaxed text-slate-400">
        Desembaraço é taxa fixa da transportadora, cobrada em qualquer destino. Simulação com
        valor contratual: não gera cotação nem reserva tarifa.
      </p>
    </div>
  );
};

const CartaoCarrier = ({
  frete,
  total,
  selecionado,
  onSelecionar,
  menorFrete,
  menorTotal,
  maisRapido,
}: {
  frete: FreteSimulado;
  total?: TotalSimulado;
  selecionado: boolean;
  onSelecionar: () => void;
  menorFrete: boolean;
  menorTotal: boolean;
  maisRapido: boolean;
}) => (
  <button
    type="button"
    onClick={onSelecionar}
    aria-pressed={selecionado}
    className={`rounded-xl border-2 bg-white p-3 text-left transition-colors ${
      selecionado ? "border-brand-sidebar" : "border-slate-200 hover:border-slate-300"
    }`}
  >
    <div className="flex items-start gap-2">
      <span
        className="grid h-7 w-7 shrink-0 place-items-center rounded text-[9px] font-bold text-white"
        style={{ backgroundColor: COR_CARRIER[frete.carrier] ?? "#64748b" }}
      >
        {SIGLA_CARRIER[frete.carrier] ?? frete.carrier}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-900">
          {NOME_CARRIER[frete.carrier] ?? frete.carrier}
        </span>
        <span className="block truncate text-[11px] text-slate-400">{frete.servico}</span>
      </span>
      <span
        className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full border-2 ${
          selecionado ? "border-brand-sidebar" : "border-slate-300"
        }`}
      >
        {selecionado && <span className="h-2 w-2 rounded-full bg-brand-sidebar" />}
      </span>
    </div>

    <div className="mt-2 flex flex-wrap gap-1">
      {menorTotal && <Etiqueta texto="Menor custo total" tom="verde" />}
      {menorFrete && <Etiqueta texto="Menor frete" tom="azul" />}
      {maisRapido && <Etiqueta texto="Mais rápido" tom="amarelo" />}
    </div>

    <div className="mt-2.5 flex items-end justify-between gap-2">
      <span>
        <span className="block text-[10px] text-slate-400">Total estimado</span>
        <span className="block text-xl font-bold leading-tight text-slate-900">
          {usd(total?.total_usd)}
        </span>
      </span>
      <span className="text-right">
        <span className="block text-[10px] text-slate-400">Prazo</span>
        <span className="block text-xs font-medium text-slate-700">
          {frete.prazo_dias} dias úteis
        </span>
      </span>
    </div>

    <dl className="mt-2.5 space-y-1 border-t border-slate-100 pt-2 text-[11px]">
      <LinhaValor termo="Frete base" valor={usd(total?.frete_usd ?? frete.base)} />
      <LinhaValor termo="Sobretaxas" valor={moedaFmt(frete.surcharges, frete.moeda)} />
      <LinhaValor termo="Desembaraço" valor={usd(total?.desembaraco_usd)} />
      <LinhaValor
        termo="Impostos do destino"
        valor={total?.imposto_usd == null ? "indisponível" : usd(total.imposto_usd)}
        alerta={total?.imposto_usd == null}
      />
    </dl>
  </button>
);

const LinhaValor = ({
  termo,
  valor,
  alerta,
}: {
  termo: string;
  valor: string;
  alerta?: boolean;
}) => (
  <div className="flex items-center justify-between gap-2">
    <dt className="text-slate-400">{termo}</dt>
    <dd className={alerta ? "font-medium text-amber-600" : "font-medium text-slate-700"}>
      {valor}
    </dd>
  </div>
);

const Etiqueta = ({ texto, tom }: { texto: string; tom: "verde" | "azul" | "amarelo" }) => {
  const cores = {
    verde: "bg-emerald-50 text-emerald-700",
    azul: "bg-blue-50 text-blue-700",
    amarelo: "bg-amber-50 text-amber-700",
  };
  return (
    <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${cores[tom]}`}>
      {texto}
    </span>
  );
};

const Vazio = () => (
  <div className="flex h-full flex-col items-center justify-center px-8 text-center">
    <div className="mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-blue-50 text-brand-primary">
      <Navigation className="h-6 w-6 rotate-45" />
    </div>
    <h3 className="text-base font-semibold text-slate-900">Quanto custa levar até lá?</h3>
    <p className="mt-1 max-w-[34ch] text-xs leading-relaxed text-slate-400">
      Preencha destino, caixas e valor ao lado. Mostramos frete, desembaraço e impostos de
      cada transportadora lado a lado.
    </p>
  </div>
);

const Aguardando = () => (
  <div className="space-y-3">
    <div className="h-10 animate-pulse rounded-xl bg-slate-200/60" />
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="h-52 animate-pulse rounded-xl bg-slate-200/60" />
      <div className="h-52 animate-pulse rounded-xl bg-slate-200/60" />
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Peças do formulário                                                 */
/* ------------------------------------------------------------------ */

const Bloco = ({ titulo, children }: { titulo: string; children: ReactNode }) => (
  <section className="space-y-2.5">
    <h3 className="text-xs font-semibold text-slate-700">{titulo}</h3>
    {children}
  </section>
);

const Campo = ({
  label,
  sufixoLabel,
  valor,
  aoMudar,
  placeholder,
}: {
  label: string;
  sufixoLabel?: string;
  valor: string;
  aoMudar: (v: string) => void;
  placeholder?: string;
}) => (
  <label className="block">
    <span className="mb-1.5 block text-[11px] font-medium text-slate-500">
      {label}
      {sufixoLabel && <span className="ml-1 text-slate-400">{sufixoLabel}</span>}
    </span>
    <input
      value={valor}
      onChange={(e) => aoMudar(e.target.value)}
      placeholder={placeholder}
      className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-300 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
    />
  </label>
);

const Seletor = ({
  label,
  valor,
  aoMudar,
  opcoes,
  placeholder,
}: {
  label: string;
  valor: string;
  aoMudar: (v: string) => void;
  opcoes: { value: string; label: string }[];
  placeholder: string;
}) => (
  <label className="block min-w-0">
    <span className="mb-1.5 block text-[11px] font-medium text-slate-500">{label}</span>
    <select
      value={valor}
      onChange={(e) => aoMudar(e.target.value)}
      className="h-10 w-full cursor-pointer rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700 outline-none transition-colors focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
    >
      <option value="">{placeholder}</option>
      {opcoes.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);
